import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

interface User {
  name: string | undefined;
  email: string;
  from: string;
}

interface LocalStorageEvents {
  roles: boolean;
  teams: boolean;
  userId: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserBehaviorService {
  /*
    BehaviorSubject to emit user when logged in with initial state empty
  */
  private user = new BehaviorSubject<User>({
    name: '',
    email: '',
    from: ''
  });
  selectedUser = this.user.asObservable();

  /*
    BehaviorSubject to emit local storage events
    Initial state all false since roles, teams and userId are fetched when user logs in
    Components like sidebar react to setting roles, teams and userId in localStorage
  */
  private localStorageEvents = new BehaviorSubject<LocalStorageEvents>({
    roles: false,
    teams: false,
    userId: false
  });

  localStorageEvents$ = this.localStorageEvents.asObservable();

  constructor(private http: HttpClient) {}

  urlApiManagement = environment.urlApiManagement;

  setUser(user: User) {
    const previousValue = this.user.getValue();

    // Early return to not sent same user already sent
    if (previousValue.name === user.name && previousValue.email === user.email) {
      return;
    }

    this.user.next(user);
  }

  emitRolesEvent(isEventEmitted: boolean): void {
    const previousValue = this.localStorageEvents.getValue();

    this.localStorageEvents.next({
      ...previousValue,
      roles: isEventEmitted
    });
  }

  emitTeamsEvent(isEventEmitted: boolean): void {
    const previousValue = this.localStorageEvents.getValue();

    this.localStorageEvents.next({
      ...previousValue,
      teams: isEventEmitted
    });
  }

  emitUserIdEvent(isEventEmitted: boolean): void {
    const previousValue = this.localStorageEvents.getValue();

    this.localStorageEvents.next({
      ...previousValue,
      userId: isEventEmitted
    });
  }

  // reset local storage events when user is not logged in anymore
  resetLocalStorageEvent(): void {
    this.localStorageEvents.next({
      roles: false,
      teams: false,
      userId: false
    });
  }

  async fromBehavior() {
    let user: any;
    this.selectedUser.subscribe((value) => (user = value));

    if (user?.from === 'store') {
      const expiring: any = await this.checkExpiring(user.email).toPromise();
      if (expiring[0].expired_flag === 'N') {
        return await { name: user.name, email: user.email };
      } else {
        return null;
      }
    } else {
      return await { name: user.name, email: user.email };
    }
  }

  fromBehaviorObservable(): Observable<User | null> {
    return this.selectedUser.pipe(
      switchMap((user) => {
        if (user && user.from === 'msal') {
          return of(user);
        }

        if (user && user.from === 'store') {
          this.checkExpiring(user.email).pipe(
            map((expiring: any) => {
              if (expiring[0].expired_flag === 'N') {
                return of(user);
              } else {
                return of(null);
              }
            })
          );
        }

        return of(null);
      })
    );
  }

  checkExpiring(email: string) {
    const url = `${this.urlApiManagement}/api/v1/logins/expired/${email}`;
    return this.http.get(url);
  }
}
